import { type ChangeEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { FileDropzone } from "../delete-saved-payment-dialog/file-dropzone/FileDropzone";
import { BankStatementFileCard } from "./bank-statement-file-card.tsx/BankStatementFileCard";
import { BankStatementUploadDescription } from "./bank-statement-upload-description/BankStatementUploadDescription";
import { UploadStatus } from "./enums/uploadStatus";
import { Typo } from "@pepperstone/pepper-ui-kit";

const MAX_FILE_SIZE_MB = 10;
const SUPPORTED_FORMATS = ["image/png", "image/jpeg", "application/pdf"];
const SUPPORTED_EXTENSIONS = [".png", ".jpeg", ".jpg", ".pdf"];

export type UploadedFile = {
  id: string;
  file: File;
  name: string;
  status: UploadStatus;
  progress: number;
  errorMessage?: string;
  document_id?: string;
};

type BankStatementUploadProps = {
  label?: string;
  file?: UploadedFile;
  onFileChange: (file: UploadedFile | undefined) => void;
};

export const BankStatementUpload = ({
  label,
  file,
  onFileChange,
}: BankStatementUploadProps) => {
  const { t } = useTranslation();
  const testId = "bank-statement-upload";

  const getErrorMessage = useCallback(
    (isValidType: boolean, isValidSize: boolean) => {
      if (!isValidType) {
        return t("withdrawals.bank_statement.error_format");
      }
      if (!isValidSize) {
        return t("withdrawals.bank_statement.error_file_size");
      }
      return undefined;
    },
    [t]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      // Only a single document is supported. If multiple files are dropped,
      // process the first and ignore the rest.
      const selected = e.target.files?.[0];
      if (!selected) return;

      const { size, type } = selected;
      const isValidType =
        SUPPORTED_FORMATS.includes(type) ||
        SUPPORTED_EXTENSIONS.some((ext) =>
          selected.name.toLowerCase().endsWith(ext)
        );
      const isValidSize = size / (1024 * 1024) <= MAX_FILE_SIZE_MB;

      onFileChange({
        id: crypto.randomUUID(),
        file: selected,
        name: selected.name,
        status:
          isValidSize && isValidType
            ? UploadStatus.PENDING
            : UploadStatus.ERROR,
        progress: 0,
        errorMessage: getErrorMessage(isValidType, isValidSize),
      });
    },
    [getErrorMessage, onFileChange]
  );

  const onDelete = useCallback(() => {
    onFileChange(undefined);
  }, [onFileChange]);

  return (
    <div data-testid={testId}>
      {label && (
        <label htmlFor="file" className="ps-text-sm">
          <Typo elem="p" size="xs" type="subheading">
            {label}
          </Typo>
        </label>
      )}
      {/* Once a document has been processed (regardless of upload outcome) the
          dropzone is hidden so only a single file can ever be uploaded. The
          file card's delete action clears it and restores the dropzone. */}
      {file ? (
        <BankStatementFileCard
          testId={`${testId}-card`}
          file={file}
          progress={file.progress}
          status={file.status}
          errorMessage={file.errorMessage}
          successMessage={t("withdrawals.bank_statement.upload_success")}
          onDelete={onDelete}
        />
      ) : (
        <FileDropzone
          testId={testId}
          uploadProps={{
            accept: SUPPORTED_FORMATS.join(","),
            multiple: true,
          }}
          onChange={handleChange}
        >
          <FileDropzone.FileDropzoneIcon testId={testId} icon="upload-cloud" />
          <FileDropzone.FileDropzoneText
            testId={testId}
            dropFilesText={t("withdrawals.bank_statement.drop_files")}
            browseFilesText={t("withdrawals.bank_statement.browse_files")}
            fileLimitationsText={t(
              "withdrawals.bank_statement.upload_file_limitations"
            )}
          />
        </FileDropzone>
      )}
      <BankStatementUploadDescription testId={testId} />
    </div>
  );
};
